import { useEffect, useState } from "react";

/**
 * Single source of truth for "is the native bridge live yet?".
 *
 * Why this exists
 * ---------------
 * The web app is loaded remotely inside a Capacitor WebView. On Android the
 * native shell injects `window.circadiaapp` in `onPageFinished` — i.e. AFTER
 * the React app has already booted — so any component that snapshots native
 * availability at module-load or first-mount time sees "web mode" and stays
 * that way for the whole session, even though the bridge lands a few hundred
 * milliseconds later. That's the root cause of "native features aren't always
 * available on Android".
 *
 * How it works
 * ------------
 * The native injection scripts dispatch a `circadiaapp:ready` window event
 * immediately after assigning `window.circadiaapp`. We listen for it and, as a
 * belt-and-suspenders fallback for older native binaries that predate the
 * event (or a missed-event race), also poll briefly until the bridge appears.
 * Once ready we latch to `true` for the rest of the session and tear the
 * watchers down.
 *
 * Consumers use `useCircadiaBridgeReady()` in React, or the non-hook
 * `subscribeCircadiaBridgeReady` / `isCircadiaBridgeReady` helpers elsewhere.
 */

type Listener = (ready: boolean) => void;

const READY_EVENT = "circadiaapp:ready";
// Poll fallback window. The bridge normally lands within a few hundred ms of
// page load; keep watching for a while in case of a slow cold start.
const POLL_INTERVAL_MS = 150;
const POLL_TIMEOUT_MS = 10_000;

function bridgePresent(): boolean {
  return (
    typeof window !== "undefined" &&
    !!(window as Window & { circadiaapp?: unknown }).circadiaapp
  );
}

let ready = bridgePresent();
const listeners = new Set<Listener>();
let watchInstalled = false;
let pollTimer: ReturnType<typeof setInterval> | null = null;
let pollDeadline = 0;

function teardownWatch(): void {
  if (!watchInstalled) return;
  watchInstalled = false;
  if (typeof window !== "undefined") {
    window.removeEventListener(READY_EVENT, handleReadyEvent);
  }
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

function markReady(): void {
  if (ready) return;
  ready = true;
  const current = Array.from(listeners);
  listeners.clear();
  teardownWatch();
  current.forEach((l) => {
    try {
      l(true);
    } catch {
      /* a bad subscriber must not break the others */
    }
  });
}

function handleReadyEvent(): void {
  if (bridgePresent()) markReady();
}

function installWatch(): void {
  if (ready || watchInstalled || typeof window === "undefined") return;
  watchInstalled = true;
  window.addEventListener(READY_EVENT, handleReadyEvent);
  pollDeadline = Date.now() + POLL_TIMEOUT_MS;
  pollTimer = setInterval(() => {
    if (bridgePresent()) {
      markReady();
      return;
    }
    if (Date.now() > pollDeadline) teardownWatch();
  }, POLL_INTERVAL_MS);
}

/** Synchronous check — true once the native bridge is present. */
export function isCircadiaBridgeReady(): boolean {
  if (ready) return true;
  if (bridgePresent()) {
    markReady();
    return true;
  }
  return false;
}

/**
 * Subscribe to a one-shot "bridge became ready" notification. Fires
 * immediately (and synchronously) if the bridge is already live. Returns an
 * unsubscribe function.
 */
export function subscribeCircadiaBridgeReady(cb: Listener): () => void {
  if (isCircadiaBridgeReady()) {
    cb(true);
    return () => {
      /* already fired */
    };
  }
  listeners.add(cb);
  installWatch();
  return () => {
    listeners.delete(cb);
  };
}

/**
 * React hook returning whether the native bridge is available. Starts as the
 * current state and flips to `true` once the bridge lands (via the ready event
 * or the poll fallback), triggering a re-render so gated UI/effects update.
 */
export function useCircadiaBridgeReady(): boolean {
  const [isReady, setIsReady] = useState<boolean>(() =>
    isCircadiaBridgeReady()
  );

  useEffect(() => {
    if (isReady) return;
    return subscribeCircadiaBridgeReady(() => setIsReady(true));
  }, [isReady]);

  return isReady;
}
